import React, { useCallback, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@jutro/components';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { CONSTANTS, useCanJumpToWizardStepDispatch } from 'wmic-pe-portals-utils-js';
import metadata from './WMICHOCustomCoverageTerm.metadata.json5';

function WMICHOCustomCoverageTerm(props) {
    const {
        id,
        addButtonLabel,
        coverageItemsVM,
        detailViewComponent,
        dtoType,
        dwellingVM,
        isDeleteDisabledForLastItem = true,
        onComponentValidation,
        onConfirmRemove,
        onUpdateJobAndCoverage,
        onValidationChange: parentOnValidate,
        readOnly,
        showErrors: parentShowErrors,
        VMData,
    } = props;
    const ViewModelService = useContext(ViewModelServiceContext);
    const { isComponentValid, onValidate, registerComponentValidation } = useValidation(id);
    const { wizardStepDispatch } = useCanJumpToWizardStepDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [selectedItem, setSelectedItem] = useState(undefined);
    const [addingItem, setAddingItem] = useState(false);
    const [selectedItemIndex, setSelectedItemIndex] = useState(-1);

    useEffect(() => {
        if (onComponentValidation) {
            registerComponentValidation(onComponentValidation);
        }
    }, [onComponentValidation, registerComponentValidation]);

    useEffect(() => {
        if (parentOnValidate) {
            parentOnValidate(isComponentValid, id);
        }
        if (isComponentValid === false) {
            wizardStepDispatch({ type: CONSTANTS.WIZARD_ACTION_TYPES.VALIDATE_PAGE });
        }
        return () => (parentOnValidate ? parentOnValidate(true, id) : undefined);
    }, [id, isComponentValid, parentOnValidate, wizardStepDispatch]);

    const updateJobAndCoverage = async () => {
        setIsLoading(true);
        try {
            await onUpdateJobAndCoverage();
        } finally {
            setIsLoading(false);
        }
    };

    const onAddOrEditItem = useCallback((itemVM, index) => {
        setSelectedItem(itemVM);
        setSelectedItemIndex(index);
        setEditingIndex(index);
        setAddingItem(true);
    }, []);

    const onCreateItem = useCallback(() => {
        const newItemVM = ViewModelService.create({}, 'pc', dtoType, dwellingVM.aspects.context());
        onAddOrEditItem(newItemVM, -1);
    }, [ViewModelService, dwellingVM.aspects, dtoType, onAddOrEditItem]);

    useEffect(() => {
        if ((coverageItemsVM?.value || []).length === 0) {
            onCreateItem();
        }
    }, [coverageItemsVM?.value, onCreateItem]);

    const onEditItem = (itemVM, index) => {
        onAddOrEditItem(ViewModelService.clone(itemVM), index);
    };

    const onRemoveItem = async (item, index) => {
        const isRemoveConfirmed = !onConfirmRemove || (await onConfirmRemove(item));
        if (isRemoveConfirmed) {
            coverageItemsVM.value.splice(index, 1);
            await updateJobAndCoverage();
        }
    };

    const onCancelItem = () => {
        setSelectedItem(undefined);
        setSelectedItemIndex(-1);
        setEditingIndex(-1);
        setAddingItem(false);
    };

    const onSaveItem = async () => {
        if (selectedItemIndex >= 0) {
            coverageItemsVM.value[selectedItemIndex] = selectedItem.value;
        } else {
            coverageItemsVM.value.push(selectedItem.value);
        }
        await updateJobAndCoverage();
        setSelectedItemIndex(-1);
        setEditingIndex(-1);
        setAddingItem(false);
    };

    const overrideProps = {
        '@field': {
            parentNode: dwellingVM,
            readOnly,
        },
        coverageItemsList: {
            VMList: coverageItemsVM?.children || [],
            VMData,
            clickable: false,
            flatCards: true,
            isDeleteDisabled: isDeleteDisabledForLastItem && (coverageItemsVM?.value || []).length <= 1,
            isEditing: addingItem,
            onEditAction: onEditItem,
            onRemoveAction: onRemoveItem,
            selectedCardIndex: editingIndex,
            readOnly,
            updateSelectedCardIndex: setEditingIndex,
        },
        addCoverageButton: {
            content: addButtonLabel,
            visible: !readOnly && !addingItem,
        },
        detailViewComponent: {
            dwellingVM,
            itemVM: selectedItem,
            visible: addingItem,
            onSaveItem,
            onCancelItem,
            onValidate,
            showErrors: parentShowErrors,
        },
    };

    const resolvers = {
        resolveCallbackMap: {
            onCreateItem,
        },
        resolveComponentMap: {
            DetailViewComponent: detailViewComponent,
        },
    };

    return (
        <Loader loaded={!isLoading}>
            <ViewModelForm
                uiProps={metadata.componentContent}
                model={dwellingVM}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                onValidationChange={onValidate}
                showErrors={parentShowErrors}
            />
        </Loader>
    );
}

WMICHOCustomCoverageTerm.propTypes = {
    id: PropTypes.string.isRequired,
    addButtonLabel: PropTypes.object.isRequired,
    coverageItemsVM: PropTypes.object.isRequired,
    detailViewComponent: PropTypes.elementType.isRequired,
    dtoType: PropTypes.string.isRequired,
    dwellingVM: PropTypes.object.isRequired,
    isDeleteDisabledForLastItem: PropTypes.bool,
    onComponentValidation: PropTypes.func,
    onConfirmRemove: PropTypes.func,
    onUpdateJobAndCoverage: PropTypes.func.isRequired,
    onValidationChange: PropTypes.func,
    readOnly: PropTypes.bool,
    showErrors: PropTypes.bool,
    VMData: PropTypes.arrayOf(
        PropTypes.shape({
            headerText: PropTypes.string,
            path: PropTypes.string,
            getData: PropTypes.func,
            isTypekey: PropTypes.bool,
            visibilityCondition: PropTypes.func,
        })
    ).isRequired,
};

export default WMICHOCustomCoverageTerm;
